/**
 * The rules themselves: what is legal, what each turn action does, and the game state
 * check. Authority is Comprehensive Rules 2.2 (SPEC.md §4); section numbers are cited
 * against the CR 2.1.0 text reviewed in `wayfinder/research/rules-mechanics-inventory.md`.
 *
 * Ticket 04 covers vanilla characters: no keywords, no abilities. Every rule that *would*
 * consult an ability goes through a named helper here, so later tickets extend rather than
 * rewrite. The bag that ticket 07 wraps around `runGameStateChecks` lives in `bag.ts` and
 * `gsc.ts`, so this module stays free of it.
 */
import {
  abilitiesOf,
  legalActivations,
  pushAbility,
  registerAbilitiesFor,
  setFreePlay,
  setFreeShift,
  setPaidPlay,
} from './abilities/interpret.ts';
import { hasKeyword } from './abilities/keywords.ts';
import type { AbilityInstance } from './bag.ts';
import type { Action, ChoiceOption } from './choices.ts';
import { option } from './choices.ts';
import {
  canChallenge,
  canQuest,
  canReady,
  damageDealtBy,
  definitionOf,
  loreOf,
  willpowerOf,
} from './characteristics.ts';
import type { PaymentPlan } from './costs.ts';
import {
  INK_PAYMENT,
  consumeOneShotModifiers,
  consumeOnceThisTurnCostModifiers,
  costOf,
  isPlayForbidden,
  paymentAdjustment,
  turnActionInkCostOf,
  shiftBaseSets,
  zonePlayPermissionFor,
} from './costs.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import {
  discardCountsAsInkable,
  drawStepSkipped,
  handCountsAsInkable,
  inkLimitOf,
  isAllowed,
  isRestricted,
  loreToWinOf,
  readyCapOf,
} from './modifiers.ts';
import { canPayFor, legalPlayOptions, legalZonePlayOptions, payFor } from './payments.ts';
import type { CardInstance, GameResult, GameState } from './state.ts';
import { instanceAt, playerAt, turnOrderFrom } from './state.ts';
import { leaveUnderPile, shiftOnto } from './stacks.ts';
import {
  availableInk,
  banishEvent,
  cardsIn,
  dealSimultaneousDamage,
  drawCard,
  emit,
  exertCharacter,
  findInHandByDef,
  gainLoreEvent,
  inkEvent,
  moveInstance,
  moveToLocation,
  payInk,
} from './zones.ts';

export { gainLore } from './zones.ts';

// ---------------------------------------------------------------------------
// Legality
// ---------------------------------------------------------------------------

/** Distinct printed ids in a player's hand, ascending — the hidden-zone option domain. */
function distinctHandDefIds(state: GameState, player: PlayerIndex): number[] {
  const seen = new Set<number>();
  for (const id of playerAt(state, player).hand) seen.add(instanceAt(state, id).defId);
  return [...seen].sort((a, b) => a - b);
}

/**
 * Every main-phase turn action available to the active player, plus ending the turn
 * (CR §3.3). Options are enumerated in a fixed order — ink, play, quest, challenge,
 * end turn — so two identical states always produce an identical option list.
 */
export function legalTurnActions(state: GameState): ChoiceOption[] {
  const player = state.activePlayer;
  const self = playerAt(state, player);
  const options: ChoiceOption[] = [];

  const handDefIds = distinctHandDefIds(state, player);
  // Ink a card — up to the turn's limit, only cards with the inkwell symbol (CR §4.2). The
  // limit is computed on read (`inkLimitOf`): 1, plus any live "ink an additional card"
  // statics the player controls right now. The symbol check has its own override point,
  // computed on read the same way: a live "all cards in your hand count as having ◉" static
  // (`handCountsAsInkable`) makes every card in this player's hand pass it.
  if (self.inksThisTurn < inkLimitOf(state, player)) {
    const handInkable = handCountsAsInkable(state, player);
    for (const defId of handDefIds) {
      const def = state.catalog.get(defId);
      if (def !== undefined && (def.inkwell || handInkable)) {
        options.push(option({ kind: 'ink', defId }));
      }
    }
    // A discard is public, so unlike the hand's defId-collapsed options above, each instance
    // is its own option (SPEC.md §4.4) — legal only while a live `discard-inkable` static
    // covers this player (`discardCountsAsInkable`), and only for cards that still pass the
    // ordinary printed-symbol check (the discard override adds a zone, not a symbol waiver).
    if (discardCountsAsInkable(state, player)) {
      for (const instance of cardsIn(state, player, 'discard')) {
        if (definitionOf(state, instance).inkwell) {
          options.push(option({ kind: 'ink-from-discard', instance: instance.id }));
        }
      }
    }
  }

  // Play a card — one option per **legal way to pay** (CR §4.3.1.2): the ink cost after every
  // payment modifier, and each alternate cost that is payable (singing, Sing Together, "for
  // free"). `payments.ts` owns the enumeration and the keys.
  options.push(...legalPlayOptions(state, player));
  // Play a card from a public zone under a continuous zone-play permission (ticket 140) — "you
  // may play characters from your discard" — re-evaluated live every time, exactly like every
  // other continuous static, so the option disappears the moment the permission lapses.
  options.push(...legalZonePlayOptions(state, player));

  // Quest and challenge — dry, ready characters only (CR §4.5, §4.6), except that Reckless
  // can't quest (CR §8.7) and Rush can challenge while drying (CR §8.8), and only when any
  // additional action cost is payable. Both lists are kept: they are exactly what the end-turn
  // compulsions below re-derive, and `legalChallengeTargets` is too expensive to run twice per
  // character.
  const questers: CardInstance[] = [];
  for (const instance of cardsIn(state, player, 'play')) {
    if (!canQuest(state, instance)) continue;
    if (availableInk(state, player) < turnActionInkCostOf(state, instance, 'quest')) continue;
    questers.push(instance);
    options.push(option({ kind: 'quest', instance: instance.id }));
  }
  const challengers: CardInstance[] = [];
  for (const instance of cardsIn(state, player, 'play')) {
    if (!canChallenge(state, instance)) continue;
    if (availableInk(state, player) < turnActionInkCostOf(state, instance, 'challenge')) continue;
    if (legalChallengeTargets(state, instance.id).length === 0) continue;
    challengers.push(instance);
    options.push(option({ kind: 'challenge', instance: instance.id }));
  }

  // Move a character of yours to a location of yours, paying its move cost (CR §4.3.6).
  options.push(...legalMoveOptions(state, player));

  // Use an activated ability (CR §4.4). Never a bag item — it resolves immediately (CR §7.7).
  for (const activation of legalActivations(state, player)) {
    options.push(option({ kind: 'activate', ...activation }));
  }

  // "You can't declare the end of your turn while a Reckless character is ready and able to
  // challenge" (CR §8.7) — and the same for a character told it "must quest if able". The
  // same predicates as `hasCompulsoryChallenge`/`hasCompulsoryQuest`, read off the two lists
  // this enumeration already built.
  const compelled =
    challengers.some((instance) => hasKeyword(state, instance, 'Reckless')) ||
    questers.some((instance) => isRestricted(state, instance, 'must-quest'));
  if (!compelled) {
    options.push(option({ kind: 'end-turn' }));
  }
  return options;
}

/**
 * Moving to a location (CR §4.3.6): a turn action that costs the **location's** move cost in
 * ink and nothing else. A character need not be ready or dry to move — moving is not exerting
 * — and a character already somewhere may move again, to a different location.
 *
 * Enumerated character-major, then location-major, so two identical states list identically.
 */
export function legalMoveOptions(state: GameState, player: PlayerIndex): ChoiceOption[] {
  const options: ChoiceOption[] = [];
  const locations = cardsIn(state, player, 'play').filter(
    (card) => definitionOf(state, card).type === 'Location',
  );
  if (locations.length === 0) return options;
  const ink = availableInk(state, player);
  for (const character of cardsIn(state, player, 'play')) {
    if (definitionOf(state, character).type !== 'Character') continue;
    // "This character can't move to locations" (CR §6.6.5).
    if (isRestricted(state, character, 'move')) continue;
    for (const location of locations) {
      if (character.location === location.id) continue;
      if (moveCostOf(state, player, location, character) > ink) continue;
      options.push(option({ kind: 'move', instance: character.id, location: location.id }));
    }
  }
  return options;
}

/**
 * A location's move cost (CR §4.3.6), for a specific mover — "you pay N {I} less to move a
 * character of yours here" reads live off the destination, the same `paymentAdjustment`
 * machinery a card's own play cost uses. Absent upstream means free, which is how it reads.
 *
 * `mover` is the character being moved: a modifier with a `mover` filter ("your Pirate
 * characters may move here for free") keys on it, and never matches when it is absent.
 */
export function moveCostOf(
  state: GameState,
  player: PlayerIndex,
  location: CardInstance,
  mover?: CardInstance,
): number {
  const printed = definitionOf(state, location).moveCost ?? 0;
  return Math.max(0, printed + paymentAdjustment(state, player, location, 'move', mover));
}

/** Perform the move turn action: pay the location's move cost, then move (CR §4.3.6). */
export function moveCharacter(
  state: GameState,
  player: PlayerIndex,
  id: InstanceId,
  location: InstanceId,
): void {
  const character = instanceAt(state, id);
  const target = instanceAt(state, location);
  if (character.controller !== player || character.zone !== 'play') {
    throw new Error(`Instance ${id} is not a card player ${player} has in play`);
  }
  if (definitionOf(state, character).type !== 'Character') {
    throw new Error(`Instance ${id} is not a character`);
  }
  if (target.controller !== player || target.zone !== 'play') {
    throw new Error(`Instance ${location} is not a location player ${player} has in play`);
  }
  if (definitionOf(state, target).type !== 'Location') {
    throw new Error(`Instance ${location} is not a location`);
  }
  if (isRestricted(state, character, 'move')) {
    throw new Error(`Instance ${id} cannot move to a location`);
  }
  payInk(state, player, moveCostOf(state, player, target, character));
  // "Once during your turn, …" (CR §6.1.13.2) — spend the slot only now, on the payment that
  // actually went through, never while `legalMoveOptions` was merely pricing candidates.
  consumeOnceThisTurnCostModifiers(state, target, 'move', character);
  moveToLocation(state, id, location);
}

/** Whether a Reckless character of this player's is ready and has something to challenge. */
export function hasCompulsoryChallenge(state: GameState, player: PlayerIndex): boolean {
  for (const instance of cardsIn(state, player, 'play')) {
    if (!hasKeyword(state, instance, 'Reckless')) continue;
    if (!canChallenge(state, instance)) continue;
    if (availableInk(state, player) < turnActionInkCostOf(state, instance, 'challenge')) continue;
    if (legalChallengeTargets(state, instance.id).length > 0) return true;
  }
  return false;
}

/**
 * Whether a character of this player's has been told it "must quest if able" and still can —
 * the same shape as Reckless's compulsion, and read in the same place: the turn stays open
 * until the quest happens or stops being possible.
 */
export function hasCompulsoryQuest(state: GameState, player: PlayerIndex): boolean {
  for (const instance of cardsIn(state, player, 'play')) {
    if (!isRestricted(state, instance, 'must-quest')) continue;
    if (!canQuest(state, instance)) continue;
    if (availableInk(state, player) >= turnActionInkCostOf(state, instance, 'quest')) return true;
  }
  return false;
}

/**
 * What a declared challenger may challenge (CR §4.6.2): an **exerted** opposing character,
 * or an opposing location in any state (CR §4.6.8), after the keyword limiters:
 *
 * - **exerted** (CR §4.6.2): only an exerted character may be challenged — unless the
 *   challenger has been given permission to challenge ready ones ("they may also challenge
 *   ready characters this turn", CR §6.6.5). That is a *different* permission from Alert.
 *   `isAllowed` is asked per defending character (not once for the whole scan) because the
 *   grant can be scoped to a `condition` on the defender — "…can challenge ready **damaged**
 *   characters" (Gizmoduck - Suited Up) covers some ready characters and not others.
 * - **Evasive** (CR §8.5): can only be challenged by a character with Evasive — or with
 *   **Alert** (CR §8.1), which ignores exactly that limiter without granting Evasive. A
 *   location with Evasive (continuously granted or otherwise) is limited the same way — the
 *   keyword reads off whatever the defender is, character or location alike (CR §8.5, §4.6.8).
 * - **Bodyguard** (CR §8.2.2): if an opponent would choose one of your *characters* to
 *   challenge, they must choose a Bodyguard character if able. Applied per defending player,
 *   and only to characters — a location is not a character, so it stays challengeable.
 *
 * **Ward is not consulted**: challenging is not "choosing" for Ward purposes (CR §8.15).
 *
 * A `be-challenged` restriction (CR §6.6.5) removes its carrier whatever its type — characters
 * and locations alike ("your other locations can't be challenged").
 */
export function legalChallengeTargets(state: GameState, challenger: InstanceId): CardInstance[] {
  const attacker = instanceAt(state, challenger);
  const ignoresEvasive =
    hasKeyword(state, attacker, 'Evasive') || hasKeyword(state, attacker, 'Alert');
  const targets: CardInstance[] = [];
  for (const player of turnOrderFrom(state, state.activePlayer)) {
    if (player === attacker.controller) continue;
    const characters: CardInstance[] = [];
    const locations: CardInstance[] = [];
    for (const instance of cardsIn(state, player, 'play')) {
      const type = definitionOf(state, instance).type;
      if (type === 'Character') {
        if (!instance.exerted && !isAllowed(state, attacker, 'challenge-ready', instance)) {
          continue;
        }
        if (!ignoresEvasive && hasKeyword(state, instance, 'Evasive')) continue;
        // "Damaged characters can't challenge this character" (CR §6.1.10) — a limiter the
        // defender carries, read against this particular challenger.
        if (isRestricted(state, instance, 'be-challenged', attacker)) continue;
        characters.push(instance);
      } else if (type === 'Location') {
        // The same Evasive limiter characters go through (CR §8.5) — a location with Evasive
        // (Game Preserve - Protected Land's EASY TO MISS grants it continuously) can only be
        // challenged by a challenger with Evasive or Alert.
        if (!ignoresEvasive && hasKeyword(state, instance, 'Evasive')) continue;
        // The same `be-challenged` gate characters go through — "your other locations can't
        // be challenged" is a restriction the location carries, read against this challenger.
        if (isRestricted(state, instance, 'be-challenged', attacker)) continue;
        locations.push(instance);
      }
    }
    const guards = characters.filter((instance) => hasKeyword(state, instance, 'Bodyguard'));
    targets.push(...(guards.length > 0 ? guards : characters), ...locations);
  }
  return targets;
}

/** The cards in hand this player may still bottom while altering (CR §2.2.2). */
export function legalMulliganOptions(
  state: GameState,
  player: PlayerIndex,
  selected: readonly InstanceId[],
): ChoiceOption[] {
  const options: ChoiceOption[] = [];
  const remaining = new Set<number>();
  for (const id of playerAt(state, player).hand) {
    if (selected.includes(id)) continue;
    remaining.add(instanceAt(state, id).defId);
  }
  for (const defId of [...remaining].sort((a, b) => a - b)) {
    options.push(option({ kind: 'mulligan-bottom', defId }));
  }
  options.push(option({ kind: 'mulligan-done' }));
  return options;
}

// ---------------------------------------------------------------------------
// Turn actions
// ---------------------------------------------------------------------------

/**
 * Put a card from hand into the inkwell, facedown (CR §4.2) — ready by default, unless a
 * replacement effect says otherwise (`inkEvent`, `damage.ts`, e.g. Daisy Duck - Paranormal
 * Investigator's "cards enter opponents' inkwells exerted").
 */
export function inkCard(state: GameState, player: PlayerIndex, defId: number): void {
  const self = playerAt(state, player);
  // Read at apply time as well as at enumeration, like every other characteristic — the limit
  // may have changed in between (its source left play), and only the next ink is constrained.
  if (self.inksThisTurn >= inkLimitOf(state, player)) {
    throw new Error(`Player ${player} has reached the ink limit this turn`);
  }
  const card = findInHandByDef(state, player, defId);
  if (card === undefined) throw new Error(`Player ${player} has no card ${defId} in hand`);
  // Same read-at-apply-time discipline as the limit: the printed symbol, or a live
  // hand-wide "counts as having ◉" static the player controls right now.
  if (!definitionOf(state, card).inkwell && !handCountsAsInkable(state, player)) {
    throw new Error(`Card ${defId} has no inkwell symbol`);
  }
  self.inksThisTurn += 1;
  inkEvent(state, player, card.id, defId);
}

/**
 * Put a card from **discard** into the inkwell, facedown (CR §4.2) — the same once-per-turn
 * turn action as `inkCard` above, extended to a discard-pile origin by a live
 * `discard-inkable` static the player controls (Moana - Curious Explorer's ANCESTRAL LEGACY).
 * Everything else is shared: the once-per-turn limit (`inkLimitOf`), the printed inkwell-symbol
 * check, and the replacement-pipeline landing (`inkEvent`, `announceInked`) that both origins
 * funnel through — inking from discard is not a parallel path.
 */
export function inkCardFromDiscard(
  state: GameState,
  player: PlayerIndex,
  instance: InstanceId,
): void {
  const self = playerAt(state, player);
  // Read at apply time as well as at enumeration, like every other characteristic — both the
  // limit and the permission itself may have changed between the two (the granting source may
  // have left play), and only the next ink is constrained.
  if (self.inksThisTurn >= inkLimitOf(state, player)) {
    throw new Error(`Player ${player} has reached the ink limit this turn`);
  }
  if (!discardCountsAsInkable(state, player)) {
    throw new Error(`Player ${player} has no permission to ink from their discard`);
  }
  const card = instanceAt(state, instance);
  if (card.zone !== 'discard' || card.controller !== player) {
    throw new Error(`Card ${instance} is not in player ${player}'s discard`);
  }
  if (!definitionOf(state, card).inkwell) {
    throw new Error(`Card ${card.defId} has no inkwell symbol`);
  }
  self.inksThisTurn += 1;
  inkEvent(state, player, card.id, card.defId);
}

/**
 * Play a card from hand (CR §4.3), paying the announced way. Characters enter play ready and
 * **drying** — they may not quest, challenge or {E} until their controller's next Set step
 * (CR §3.2.2, §5.1), unless they have Rush (CR §8.8).
 *
 * `payment` is the method the option announced (CR §4.3.1.2): the ink cost by default, or one
 * of the alternate costs that replace it outright — singing, Sing Together, "for free", Shift.
 * `payments.ts` computes the total cost and takes the payment; the card moves only once it is
 * paid in full (CR §4.3.3), so an unpayable play throws rather than half-happening.
 *
 * Entering play is also where a card's triggered abilities are registered, and where a
 * **Bodyguard** character's "may enter play exerted" choice is raised (CR §8.2.1) — an
 * `enter-exerted` frame, so the decision is made before any "when you play" trigger resolves.
 */
export function playCard(
  state: GameState,
  player: PlayerIndex,
  defId: number,
  payment: PaymentPlan = INK_PAYMENT,
): void {
  const card = findInHandByDef(state, player, defId);
  if (card === undefined) throw new Error(`Player ${player} has no card ${defId} in hand`);
  // "You can't play this character unless …" (CR §6.6.5) — no payment method gets round it, so
  // it is asked here as well as at enumeration (`legalPlayOptions`).
  if (isRestricted(state, card, 'play')) {
    throw new Error(`Player ${player} can't play card ${defId}`);
  }
  if (!canPayFor(state, player, card, payment)) {
    throw new Error(`Player ${player} cannot pay for card ${defId} with a ${payment.kind} payment`);
  }
  const paid = payFor(state, player, card, payment);
  moveInstance(state, card.id, 'play');
  card.exerted = false;
  card.damage = 0;
  card.drying = true;
  // Shift lands the card on top of its bases, which stop being in play, and the states it
  // just got as a newcomer are replaced by the ones it inherits (CR §8.10.4; `stacks.ts`) —
  // true whether the Shift cost was paid in ink or by an alternate cost (ticket 136).
  if (payment.kind === 'shift' || payment.kind === 'shift-alternate-cost') {
    shiftOnto(state, card, payment.onto);
  }
  registerAbilitiesFor(state, card);
  // "This character enters play exerted" (CR §6.6.5's "can't"): read *after* the card's own
  // statics are reachable, so a card that says it about itself is affected by its own text.
  const forcedExerted = isRestricted(state, card, 'enter-play-ready');
  if (forcedExerted) card.exerted = true;
  state.playedThisTurn.push(card.id);
  emit(state, { type: 'card-played', player, instance: card.id, defId, cost: paid.inkPaid });
  // Each singer "sings" the song, which is what "whenever this character sings" reads
  // (CR §8.12's fan-out). Emitted after the song has entered play, so nothing in the event
  // stream names a card that is still hidden.
  for (const singer of paid.singers) {
    emit(state, {
      type: 'card-sung',
      player,
      instance: singer,
      defId: instanceAt(state, singer).defId,
      song: card.id,
      songDefId: defId,
    });
  }
  // …and the song itself was sung **once**, however many sang it — what "whenever one or more
  // of your characters sings a song" reads, carrying the singers it then acts on (CR §8.12).
  if (paid.singers.length > 0) {
    emit(state, {
      type: 'song-sung',
      player,
      instance: card.id,
      defId,
      singers: [...paid.singers],
    });
  }
  if (definitionOf(state, card).type === 'Action') {
    // An action's effect resolves immediately and in full, never through the bag
    // (CR §5.4.1.2, §6.7.4); the card is discarded once it finishes.
    pushAbility(state, actionInstance(state, card, paid.singers));
    return;
  }
  // The choice is "may enter play exerted" — moot, and not offered, when a printed static
  // already forces it (Flotsam - Wicked Defender, Prince Eric - Grim Groom, ticket 132):
  // declining an optional choice must never undo a forced one.
  if (hasKeyword(state, card, 'Bodyguard') && !forcedExerted) {
    state.stack.push({ kind: 'enter-exerted', instance: card.id });
  }
}

/**
 * "You may play that character for free" (CR §6.1.7) — playing a card **from wherever it is**,
 * paying nothing. The card need not be in a hand: the two cards that say it play off the top
 * of a deck.
 *
 * Everything else about entering play is the same as `playCard`, deliberately: the same drying
 * newcomer, the same registration, the same `card-played` event (at a cost of 0), the same
 * Action-resolves-immediately rule. `exerted` is the "and they enter play exerted" clause.
 *
 * Including the "can't play" check: paying nothing is still playing (CR §6.6.5), so a card a
 * restriction forbids stays where it is and the effect does as much as it can — nothing
 * (CR §1.2.3). The pools such an effect chooses from exclude it already (`Selector.playable`);
 * this is the same answer given where the play actually happens.
 *
 * `destination` carries "if you do, put that card on the bottom of your deck instead of into
 * your discard" (ticket 127) through to `enterPlayFrom`'s Action-finishing choice — only ever
 * set by `playForFreeNow`'s own `destination` option, never inferred here.
 */
export function playForFree(
  state: GameState,
  id: InstanceId,
  exerted: boolean,
  destination?: 'bottom-of-deck',
): void {
  const card = instanceAt(state, id);
  if (isRestricted(state, card, 'play')) return;
  enterPlayFrom(state, card, exerted, 0, [], destination);
}

/**
 * "Shift a character … for free" — a one-shot effect play, not a hand play action and not a
 * Shift-cost payment. Legality is nevertheless the normal Shift legality, and placement is
 * the same `shiftOnto` operation used by `playCard`, so existing piles and inherited states
 * behave identically.
 */
export function shiftForFree(state: GameState, id: InstanceId, onto: InstanceId): void {
  const card = instanceAt(state, id);
  if (isRestricted(state, card, 'play')) return;
  const legal = shiftBaseSets(state, card).some(
    (bases) => bases.length === 1 && bases[0] === onto,
  );
  if (!legal) return;
  enterPlayFrom(state, card, false, 0, [onto]);
}

/**
 * "At the start of your turn, if this card is in your discard, you may play her" — the same
 * play from wherever the card is, for its **real cost** (CR §4.3.3, §6.1.7).
 *
 * The two differences from `playForFree` are the whole point of it: the cost is read off the
 * card rather than assumed to be 0, and a cost that cannot be paid **in full** stops the play
 * entirely — a cost is never paid partially, so an unaffordable offer does nothing (CR §1.2.3,
 * §4.3.3). The offer is guarded by `can-pay-ink` before it is ever put in front of a player;
 * this is the same answer given where the payment actually happens.
 *
 * One-shot payment modifiers are spent here as they are for a play from hand (`payments.ts`):
 * a floating "the next character you play this turn costs 2 {I} less" does not survive it.
 */
export function playPaying(state: GameState, id: InstanceId, exerted: boolean): void {
  const card = instanceAt(state, id);
  const player = card.controller;
  if (isRestricted(state, card, 'play')) return;
  const cost = costOf(state, card);
  if (cost > availableInk(state, player)) return;
  payInk(state, player, cost);
  consumeOneShotModifiers(state, player, card, 'play');
  enterPlayFrom(state, card, exerted, cost);
}

/**
 * Play a card from a public zone under a continuous zone-play permission (ticket 140) — The
 * Horned King - Merciless Master's CAULDRON'S POWER: "While exerted, you may play characters
 * from your discard. If you do, they enter play exerted. (You pay all costs.)" A `turn-action`
 * choice, not a resolved effect, so it is offered repeatedly for as long as the permission holds
 * — `legalZonePlayOptions` (`payments.ts`) is the enumeration this answers.
 *
 * Everything here is **re-verified live**, exactly as `playCard` re-checks a hand play, rather
 * than trusted from the option that was offered: the permission itself (an opponent's discard
 * cleared between enumeration and this call is impossible in a synchronous engine, but a
 * defensive re-check costs nothing and matches every other play entry point), and both play
 * restrictions — `isRestricted(…, 'play')` and `isPlayForbidden` — the same two guards
 * `legalPlayOptions`/`playCard` apply to a hand play. A review of an earlier ticket found a free
 * play bypassing a restriction by skipping straight to the resolved effect; this path goes
 * through the identical checks so it cannot repeat that.
 */
export function playFromZonePermission(
  state: GameState,
  player: PlayerIndex,
  id: InstanceId,
  free: boolean,
): void {
  const card = instanceAt(state, id);
  const permission = zonePlayPermissionFor(state, player, card);
  if (permission === null || permission.cost !== (free ? 'free' : 'full')) {
    throw new Error(`Player ${player} has no permission to play card ${id} this way`);
  }
  if (isRestricted(state, card, 'play')) {
    throw new Error(`Player ${player} can't play card ${id}`);
  }
  if (isPlayForbidden(state, player, card)) {
    throw new Error(`Player ${player} can't play card ${id}`);
  }
  let cost = 0;
  if (!free) {
    cost = costOf(state, card);
    if (cost > availableInk(state, player)) {
      throw new Error(`Player ${player} cannot pay for card ${id}`);
    }
    payInk(state, player, cost);
  }
  enterPlayFrom(state, card, permission.entersExerted, cost);
}

/**
 * Entering play from wherever the card was, once its cost (if any) has been paid — the shared
 * tail of `playForFree` and `playPaying`, and deliberately the same beats as `playCard`: the
 * same drying newcomer, the same registration, the same `card-played` event, the same
 * Action-resolves-immediately rule. `exerted` is the "and they enter play exerted" clause.
 *
 * A card played from **under** another one (The Black Cauldron, ticket 63) leaves its pile
 * first — `leaveUnderPile` is the bookkeeping `moveInstance` itself does not do, because only
 * `stacks.ts` may touch `state.stacks` (the same seam `extractFromStack` uses for a return to
 * hand). A no-op for every other zone.
 *
 * `finishTo` rides straight onto the Action's own `AbilityInstance` (`actionInstance`) — where
 * ticket 127's "instead of into your discard" redirect actually lives, read back by
 * `engine.ts`'s `stepResolveAbility` once the card's own effect has finished.
 */
function enterPlayFrom(
  state: GameState,
  card: CardInstance,
  exerted: boolean,
  cost: number,
  shiftBases: readonly InstanceId[] = [],
  finishTo?: 'bottom-of-deck',
): void {
  const id = card.id;
  const player = card.controller;
  leaveUnderPile(state, id);
  moveInstance(state, id, 'play');
  card.exerted = exerted;
  card.damage = 0;
  card.drying = true;
  if (shiftBases.length > 0) shiftOnto(state, card, shiftBases);
  registerAbilitiesFor(state, card);
  const forcedExerted = isRestricted(state, card, 'enter-play-ready');
  if (forcedExerted) card.exerted = true;
  state.playedThisTurn.push(id);
  emit(state, { type: 'card-played', player, instance: id, defId: card.defId, cost });
  if (definitionOf(state, card).type === 'Action') {
    pushAbility(state, actionInstance(state, card, [], finishTo));
    return;
  }
  // The choice is "may enter play exerted" — moot, and not offered, when a printed static
  // already forces it (ticket 132): declining an optional choice must never undo a forced one.
  if (hasKeyword(state, card, 'Bodyguard') && !forcedExerted) {
    state.stack.push({ kind: 'enter-exerted', instance: card.id });
  }
}

// Wired at module load, as `setTriggerRefresh` is: the interpreter's `play-free` and
// `play-paying` effects need this module's play machinery, and this module already imports
// the interpreter.
setFreePlay(playForFree);
setFreeShift(shiftForFree);
setPaidPlay(playPaying);

/**
 * The resolvable form of an Action card's printed effect (CR §5.4).
 *
 * `singers` rides in as a binding, so a song can say "for each character that sang this song"
 * and "ready them" — the one set of cards its own sentence names that nobody chose and that no
 * selector could describe afterwards (`bag.ts`'s `eventBindings` does the same for triggers).
 *
 * `finishTo` is ticket 127's "put that card on the bottom of your deck instead of into your
 * discard" — where this Action goes once its own effect finishes, read by `engine.ts`'s
 * `stepResolveAbility` in place of the ordinary discard (CR §5.4.1.2).
 */
function actionInstance(
  state: GameState,
  card: CardInstance,
  singers: readonly InstanceId[] = [],
  finishTo?: 'bottom-of-deck',
): AbilityInstance {
  const action = abilitiesOf(state, card).find((ability) => ability.kind === 'action');
  return {
    id: -1,
    controller: card.controller,
    source: card.id,
    subject: null,
    effect: action === undefined ? [] : action.effect,
    label: action?.name ?? '',
    origin: 'action',
    ...(singers.length === 0 ? {} : { bindings: [{ id: 'singers', instances: [...singers] }] }),
    ...(finishTo === undefined ? {} : { finishTo }),
  };
}

/**
 * Exert a dry character to gain its lore (CR §4.5). Negative lore gains 0 (CR §6.6.3).
 * Reckless characters can't quest at all (CR §8.7).
 *
 * The quest itself is never in doubt — a player who "can't gain lore" may still quest, and the
 * character still exerts and still emits `character-quested`; only the gain is stopped, which
 * is `gainLoreEvent`'s question, not this one's.
 */
export function quest(state: GameState, id: InstanceId): void {
  const card = instanceAt(state, id);
  if (!canQuest(state, card)) throw new Error(`Instance ${id} cannot quest`);
  const cost = turnActionInkCostOf(state, card, 'quest');
  if (availableInk(state, card.controller) < cost) {
    throw new Error(`Instance ${id} cannot pay ${cost} ink to quest`);
  }
  payInk(state, card.controller, cost);
  consumeOneShotModifiers(state, card.controller, card, 'quest');
  consumeOnceThisTurnCostModifiers(state, card, 'quest');
  exertCharacter(state, id);
  if (!state.questedThisTurn.includes(id)) state.questedThisTurn.push(id);
  const lore = Math.max(0, loreOf(state, card));
  emit(state, { type: 'character-quested', player: card.controller, instance: id, lore });
  gainLoreEvent(state, card.controller, lore, id);
}

/** Challenge Declaration step (CR §4.6.3): exert the challenger, announce the challenge. */
export function declareChallenge(state: GameState, challenger: InstanceId, defender: InstanceId): void {
  const attacker = instanceAt(state, challenger);
  if (!canChallenge(state, attacker)) throw new Error(`Instance ${challenger} cannot challenge`);
  const cost = turnActionInkCostOf(state, attacker, 'challenge');
  if (availableInk(state, attacker.controller) < cost) {
    throw new Error(`Instance ${challenger} cannot pay ${cost} ink to challenge`);
  }
  payInk(state, attacker.controller, cost);
  consumeOneShotModifiers(state, attacker.controller, attacker, 'challenge');
  consumeOnceThisTurnCostModifiers(state, attacker, 'challenge');
  exertCharacter(state, challenger);
  // Counted before the declaration is announced, so a trigger guard reading "if it's the
  // second challenge this turn" counts the challenge that set it off (CR §4.6.3).
  state.challengedThisTurn.push(challenger);
  state.wasChallengedThisTurn.push(defender);
  emit(state, {
    type: 'challenge-declared',
    player: attacker.controller,
    challenger,
    defender,
  });
  // The same declaration from the defender's side — "whenever this character is challenged".
  const target = instanceAt(state, defender);
  emit(state, {
    type: 'card-challenged',
    player: target.controller,
    instance: defender,
    defId: target.defId,
    by: challenger,
  });
}

/**
 * Challenge Damage step (CR §4.6.5): both cards deal damage equal to their {S}
 * **simultaneously**; locations deal none. A game state check follows.
 *
 * "Simultaneously" is not just about the two amounts — both are already computed here, from
 * one unmutated `state`, before either lands — it also governs how each side's damage
 * modifiers and replacement effects get decided (CR §6.5): a replacement on either side must
 * read the board as it stood before the challenge, never as amended by the other side's damage
 * landing first. `dealSimultaneousDamage` (`damage.ts`) is what gives both that one shared
 * pre-challenge snapshot; a plain pair of `dealDamage` calls would let the first one's counters
 * land before the second one's replacements are even looked up.
 */
export function resolveChallengeDamage(
  state: GameState,
  challenger: InstanceId,
  defender: InstanceId,
): void {
  const attacker = instanceAt(state, challenger);
  const target = instanceAt(state, defender);
  const toDefender = damageDealtBy(state, attacker);
  const toChallenger = damageDealtBy(state, target);
  dealSimultaneousDamage(
    state,
    { target: defender, amount: toDefender, source: challenger },
    { target: challenger, amount: toChallenger, source: defender },
  );
}

// ---------------------------------------------------------------------------
// Turn structure
// ---------------------------------------------------------------------------

/**
 * What the Ready step is still waiting on: a live ready-cap restriction (CR §6.6.5 — "each
 * opponent can't ready more than N of their characters at the start of their next turn") that
 * covers more eligible characters than it allows, so the affected player has to choose which
 * of them ready. `null` from `readyStep` means the step finished on its own — no cap, or a cap
 * with room for everyone eligible, which is not a real decision (there is never a reason to
 * ready fewer characters than a cap permits).
 */
export interface ReadyChoicePending {
  /** The exerted, individually-readyable characters competing for the cap's few slots. */
  readonly eligible: readonly InstanceId[];
  readonly cap: number;
}

/**
 * Ready step (CR §3.2.1): ready everything the active player controls — except what a
 * continuous effect forbids ("chosen exerted character can't ready at the start of their next
 * turn") or caps ("each opponent can't ready more than N of their characters at the start of
 * their next turn", Marshmallow - Cranky Climber).
 *
 * A ready-cap only ever limits **characters** — ink and items ready as normal, and so does a
 * capped character when there are no more of them eligible than the cap allows. Only when the
 * cap actually bites does this return non-`null`, leaving the capped characters exerted for
 * the caller to turn into a `ready-choice` decision; everything else is readied here and now.
 *
 * Emits one `card-readied` per card readied here, plus the `cards-readied` batch over that
 * same set: "whenever you ready this character" is a per-card trigger, and it may fire several
 * times in a turn. A character readied later by the ready-choice decision gets its own
 * `card-readied` when it is chosen, since it was not part of this step's automatic batch.
 */
export function readyStep(state: GameState): ReadyChoicePending | null {
  const player = state.activePlayer;
  const self = playerAt(state, player);
  self.inksThisTurn = 0;
  // "…this turn" is the turn everyone is in, and an effect can put a card into an opponent's
  // inkwell, so this count resets for every seat where the per-turn lists below do.
  for (const each of state.players) each.cardsInkedThisTurn = 0;
  // Drawn-this-turn resets alongside inked-this-turn, for the same "…this turn" reason.
  for (const each of state.players) each.cardsDrawnThisTurn = 0;
  // Discard entries are likewise tracked per seat but describe the one shared current turn.
  for (const each of state.players) each.cardsPutIntoDiscardThisTurn = 0;
  // The narrower "discarded" verb count resets the same way.
  for (const each of state.players) each.cardsDiscardedThisTurn = 0;
  // "Once during your turn" / "once per turn" limits are per turn, so they reset here
  // (CR §6.1.13.2) — tracked per ability instance, which is what the keys encode.
  state.abilityUses = [];
  state.questedThisTurn = [];
  state.challengedThisTurn = [];
  state.wasChallengedThisTurn = [];
  state.playedThisTurn = [];
  state.banishedInChallengeThisTurn = [];
  state.cardsPutUnderThisTurn = [];
  state.leftDiscardThisTurn = [];
  state.banishedThisTurn = [];
  state.damagedThisTurn = [];
  state.damageRemovedThisTurn = state.players.map(() => 0);

  const cap = readyCapOf(state, player);
  const uncapped: InstanceId[] = [];
  const cappedEligible: InstanceId[] = [];
  for (const id of [...self.play, ...self.inkwell]) {
    const card = instanceAt(state, id);
    if (!card.exerted) continue;
    // "That character can't ready at the start of their next turn" (CR §6.6.5) — a continuous
    // effect that takes away this step's permission for one card.
    if (!canReady(state, card)) continue;
    if (cap !== null && card.zone === 'play' && definitionOf(state, card).type === 'Character') {
      cappedEligible.push(id);
    } else {
      uncapped.push(id);
    }
  }
  if (cap !== null && cappedEligible.length > cap) {
    for (const id of uncapped) readyOne(state, player, id);
    emit(state, { type: 'cards-readied', player, instances: uncapped });
    return { eligible: cappedEligible, cap };
  }
  const readied = [...uncapped, ...cappedEligible];
  for (const id of readied) readyOne(state, player, id);
  emit(state, { type: 'cards-readied', player, instances: readied });
  return null;
}

/** Ready one card and narrate it — the shared tail of both `readyStep`'s paths. */
function readyOne(state: GameState, player: PlayerIndex, id: InstanceId): void {
  const card = instanceAt(state, id);
  card.exerted = false;
  emit(state, { type: 'card-readied', player, instance: id, defId: card.defId });
}

/**
 * Set step (CR §3.2.2): characters stop drying, then each location the active player has in
 * play pays out its ◊ — the second way lore is gained, and the reason a location is worth
 * building a board around.
 */
export function setStep(state: GameState): void {
  const player = state.activePlayer;
  const dried: InstanceId[] = [];
  for (const card of cardsIn(state, player, 'play')) {
    if (!card.drying) continue;
    card.drying = false;
    dried.push(card.id);
  }
  emit(state, { type: 'characters-dried', player, instances: dried });
  for (const card of cardsIn(state, player, 'play')) {
    if (definitionOf(state, card).type !== 'Location') continue;
    const lore = Math.max(0, loreOf(state, card));
    if (lore > 0) gainLoreEvent(state, player, lore, card.id);
  }
}

/**
 * Draw step (CR §3.2.3). The starting player skips it on the game's first turn (§3.2.3.1) —
 * the only turn-number-dependent rule in the core loop — and a player controlling a live
 * `skip-draw` static skips it every turn ("Skip your turn's Draw step", Arthur - Determined
 * Squire), read live by `drawStepSkipped`.
 */
export function drawStep(state: GameState): void {
  if (state.turn === 1 && state.activePlayer === state.startingPlayer) return;
  if (drawStepSkipped(state, state.activePlayer)) return;
  drawCard(state, state.activePlayer);
}

// ---------------------------------------------------------------------------
// Game state check (CR §1.8)
// ---------------------------------------------------------------------------

export interface GameStateCheckOptions {
  /**
   * True for the check that runs as the turn ends — the only moment the empty-deck loss
   * applies (CR §1.8.1.2, changed in CR 2.0: drawing from an empty deck is not a loss).
   */
  readonly turnEnding?: boolean;
}

/**
 * Run game state checks until they find nothing (CR §1.8.3: a completed check immediately
 * runs again). Triggers raised by the cascade wait for it to finish (CR §1.8.2) — collecting
 * them into the bag is `gsc.ts`'s `settle`, which is what callers outside this module use.
 */
export function runGameStateChecks(state: GameState, options: GameStateCheckOptions = {}): void {
  for (let guard = 0; guard < 128; guard += 1) {
    if (state.result !== null) return;

    // Win by lore (CR §1.8.1.1). Results across players apply in turn order (§1.8.4). The
    // threshold is per-player (`loreToWinOf`) — "Opponents need 25 lore to win the game" raises
    // it for whoever it covers, `state.rules.loreToWin` unchanged for everyone else.
    const winners = turnOrderFrom(state, state.activePlayer).filter(
      (player) => playerAt(state, player).lore >= loreToWinOf(state, player),
    );
    if (winners.length === 1) {
      endGame(state, { kind: 'win', winner: winners[0] as PlayerIndex, reason: 'lore' });
      return;
    }
    if (winners.length > 1) {
      endGame(state, { kind: 'draw', reason: 'simultaneous' });
      return;
    }

    // Deck-out (CR §1.8.1.2): you lose only if your turn *ends* with an empty deck.
    if (options.turnEnding === true && playerAt(state, state.activePlayer).deck.length === 0) {
      const survivors = state.players
        .map((player) => player.index)
        .filter((player) => player !== state.activePlayer);
      if (survivors.length === 1) {
        endGame(state, { kind: 'win', winner: survivors[0] as PlayerIndex, reason: 'deck-out' });
      } else {
        endGame(state, { kind: 'draw', reason: 'simultaneous' });
      }
      return;
    }

    // Banish anything with damage at or above its Willpower (CR §1.8.1.4). `banishEvent` may
    // redirect a card a `banish` replacement catches (ticket 137) — it still leaves play either
    // way, so re-running the checks below is correct regardless of what it became.
    let banished = false;
    for (const player of turnOrderFrom(state, state.activePlayer)) {
      for (const card of cardsIn(state, player, 'play')) {
        const type = definitionOf(state, card).type;
        if (type !== 'Character' && type !== 'Location') continue;
        if (card.damage < willpowerOf(state, card)) continue;
        banishEvent(state, card.id);
        banished = true;
      }
    }
    if (!banished) return;
  }
  throw new Error('Game state checks did not reach quiescence');
}

export function endGame(state: GameState, result: GameResult): void {
  state.result = result;
  state.phase = 'game-over';
  emit(state, { type: 'game-ended', result });
}

/** Resolve an action's card reference for the engine's own error messages. */
export function describeAction(action: Action): string {
  return JSON.stringify(action);
}
